import React, { useState, useMemo, useEffect } from 'react'

import { ArrowLeft, HelpCircle } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { BillingInformationFormData } from '@/features/account/billing'
import { AccountSuspendedModal } from '@/features/account/components/AccountSuspendedModal'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Switch } from '@/shared/components/atoms/Switch'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { ComparePlans } from './components/ComparePlans'
import { PlanCard } from './components/PlanCard'
import {
	SubscriptionStepper,
	type SubscribeState,
} from './components/SubscriptionStepper'
import { SubscriptionSummary } from './components/SubscriptionSummary'
import { WhatIsZoneModal } from './components/WhatIsZoneModal'
import { useMobileSubscribeStepScrollTop } from './hooks/useMobileSubscribeStepScrollTop'
import { useSubscribeGuard } from './hooks/useSubscribeGuard'
import { BillingCycle, PlanConfig, PlanType } from './types'

// Note: Descriptions will be loaded from translations
const PLANS_BASE: Omit<PlanConfig, 'description'>[] = [
	{
		id: 'essential',
		name: 'Essential',
		priceMonthly: 15,
		priceAnnual: 12,
		currency: 'USD',
		hasZoneSelector: true,
	},
	{
		id: 'advanced',
		name: 'Advanced',
		priceMonthly: 30,
		priceAnnual: 24,
		currency: 'USD',
		hasZoneSelector: true,
	},
	{
		id: 'signature',
		name: 'Signature',
		priceMonthly: 0,
		priceAnnual: 0,
		currency: 'USD',
		hasZoneSelector: false,
		isContactUs: true,
	},
]

interface LocationState {
	plan?: PlanConfig | string
	billingCycle?: BillingCycle | string
	zonesCount?: number
	termsAccepted?: boolean
	[key: string]: unknown
}

const ChooseSubscriptionPage: React.FC = () => {
	useSubscribeGuard()
	useMobileSubscribeStepScrollTop()
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState | null
	const account = useCurrentAccount()

	// Get current subscription's zone quantity from items
	const currentSubscriptionZones = useMemo(() => {
		if (!account?.subscriptions) return 1

		const activeSubscription = account.subscriptions.find(
			(sub) => sub.status === 'active' || sub.status === 'in_trial'
		)

		if (!activeSubscription) return 1

		// Get player quantity from subscription items
		const playerQuantity = activeSubscription.getPlayerQuantity()
		return playerQuantity || 1
	}, [account?.subscriptions])

	// Get the number of active zones (players count) - this is the minimum allowed
	const activeZonesCount = useMemo(() => {
		return account?.playersCount || 0
	}, [account?.playersCount])

	// Minimum zones is the greater of 1 or active zones count
	const minZonesCount = useMemo(() => {
		return Math.max(1, activeZonesCount)
	}, [activeZonesCount])

	// Build PLANS with translated descriptions
	const PLANS: PlanConfig[] = React.useMemo(
		() =>
			PLANS_BASE.map((plan) => ({
				...plan,
				description: t(`subscribe.plan_descriptions.${plan.id}`),
			})),
		[t]
	)

	const [billingCycle, setBillingCycle] = useState<BillingCycle>(() => {
		const raw = state?.billingCycle
		return (raw as BillingCycle) || 'annual'
	})

	const [selectedPlan, setSelectedPlan] = useState<PlanType>(() => {
		const raw = state?.plan
		const planId =
			raw && typeof raw === 'object' && 'id' in raw
				? ((raw as PlanConfig).id as PlanType)
				: (raw as PlanType) || 'essential'

		return (
			PLANS.some((p) => p.id === planId) ? planId : 'essential'
		) as PlanType
	})

	const [zonesCount, setZonesCount] = useState(
		() => state?.zonesCount || currentSubscriptionZones
	)

	const [termsAccepted, setTermsAccepted] = useState(
		() => state?.termsAccepted || false
	)

	// Ensure zonesCount is never below the minimum when activeZonesCount changes
	useEffect(() => {
		if (zonesCount < minZonesCount) {
			setZonesCount(minZonesCount)
		}
	}, [zonesCount, minZonesCount])
	const [showWhatIsZoneModal, setShowWhatIsZoneModal] = useState(false)
	const [isContinuing, setIsContinuing] = useState(false)

	const allSubscriptions = account?.subscriptions ?? []
	const hasRealSubscription = allSubscriptions.some((sub) => sub.total > 0)

	// Paying customer whose account got suspended — non-dismissible, must contact team.
	const showSuspendedModal = account?.suspended === true && hasRealSubscription

	// Trial/inbound user whose trial expired — dismissible, can choose a plan.
	const TRIAL_MODAL_DISMISSED_KEY = 'bt_trial_modal_dismissed'
	const [isTrialModalDismissed, setIsTrialModalDismissed] = useState(
		() => sessionStorage.getItem(TRIAL_MODAL_DISMISSED_KEY) === 'true'
	)
	const showTrialExpiredModal =
		account?.suspended === true &&
		!hasRealSubscription &&
		!isTrialModalDismissed
	const handleDismissTrialModal = () => {
		sessionStorage.setItem(TRIAL_MODAL_DISMISSED_KEY, 'true')
		setIsTrialModalDismissed(true)
	}

	const selectedPlanConfig =
		PLANS.find((p) => p.id === selectedPlan) ?? PLANS[0]

	const handleContinue = () => {
		setIsContinuing(true)
		// Navigate to billing step with subscription state
		navigate('/subscribe/billing', {
			state: {
				...(state ?? {}),
				plan: selectedPlanConfig,
				billingCycle,
				zonesCount,
				termsAccepted,
			},
		})
	}

	const handleContactUs = () => {
		window.open('https://wa.me/15557052438', '_blank', 'noopener,noreferrer')
	}

	const handleBack = () => {
		navigate(-1)
	}

	const stepperDraft: SubscribeState = {
		plan: selectedPlanConfig.id,
		billingCycle,
		zonesCount,
		billingDraft: state?.billingDraft as BillingInformationFormData | undefined,
	}

	return (
		<AuthenticatedLayout hideSidebar topAppBarContent={<></>}>
			<div className="min-h-screen bg-background">
				{/* Header */}
				<div className="border-b border-border">
					<div className="max-w-7xl mx-auto px-6 py-4">
						<div className="flex items-center gap-4">
							{!account?.suspended && (
								<button
									onClick={handleBack}
									className="text-muted-foreground hover:text-foreground transition-colors"
								>
									<ArrowLeft className="h-5 w-5" />
								</button>
							)}
							<h1 className="text-xl font-medium text-muted-foreground">
								{t('subscribe.title')}
								{/* <span className="text-foreground"> | </span>
								<span className="text-foreground">
									{t('subscribe.choose_subscription')}
								</span> */}
							</h1>
						</div>
					</div>
				</div>

				{/* Stepper */}
				<div className="border-b border-border py-6 hidden lg:block">
					<SubscriptionStepper currentStep="choose" draft={stepperDraft} />
				</div>

				{/* Main Content */}
				<div className="max-w-7xl mx-auto px-6 py-8">
					<div className="flex gap-8 flex-col lg:flex-row">
						{/* Left Column - Plan Selection */}
						<div className="flex-1">
							{/* Billing Toggle */}
							<div className="flex items-center gap-3 mb-8">
								<Switch
									checked={billingCycle === 'annual'}
									onCheckedChange={(checked) =>
										setBillingCycle(checked ? 'annual' : 'monthly')
									}
								/>
								<span className="font-medium">
									{t('subscribe.annual_subscription')}
								</span>
								<Badge
									variant="zones"
									className="bg-main-accent text-primary text-xs"
								>
									{t('subscribe.discount')}
								</Badge>
							</div>

							{/* Plan Selection Header */}
							<div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4">
								<h2 className="text-lg font-semibold">
									{t('subscribe.choose_your_subscription')}
								</h2>
								<Button
									variant="ghost"
									size="sm"
									className="text-sm flex justify-start lg:block"
									onClick={() => setShowWhatIsZoneModal(true)}
								>
									<HelpCircle className="h-4 w-4 mr-1" />
									{t('subscribe.what_is_zone')}
								</Button>
							</div>

							{/* Plan Cards */}
							<div className="space-y-4">
								{PLANS.map((plan) => (
									<PlanCard
										key={plan.id}
										plan={plan}
										isSelected={selectedPlan === plan.id}
										billingCycle={billingCycle}
										zonesCount={zonesCount}
										minZones={minZonesCount}
										onSelect={() => setSelectedPlan(plan.id)}
										onZonesChange={setZonesCount}
										onContactUs={handleContactUs}
									/>
								))}
							</div>

							{/* Compare Plans */}
							<ComparePlans />
						</div>

						{/* Right Column - Summary */}
						<div className="w-full lg:w-80 flex-shrink-0">
							<div className="sticky top-8">
								<SubscriptionSummary
									plan={selectedPlanConfig}
									billingCycle={billingCycle}
									zonesCount={zonesCount}
									onContinue={handleContinue}
									isLoading={isContinuing}
									isTermsAccepted={termsAccepted}
									onTermsAcceptedChange={setTermsAccepted}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* What is a zone modal */}
			<WhatIsZoneModal
				visible={showWhatIsZoneModal}
				onClose={() => setShowWhatIsZoneModal(false)}
			/>

			{/* Paying customer suspended — contact team, non-dismissible */}
			<AccountSuspendedModal visible={showSuspendedModal} />

			{/* Trial/inbound user suspended — choose a plan, dismissible */}
			<AccountSuspendedModal
				visible={showTrialExpiredModal}
				variant="trial"
				onDismiss={handleDismissTrialModal}
			/>
		</AuthenticatedLayout>
	)
}

export default ChooseSubscriptionPage
